import React, { useMemo, useState } from 'react';

import Check from '@rover/icons/svg/check.svg';
import { Box, Flex, Image } from '@rover/kibble/core';
import { Props as BoxProps } from '@rover/kibble/core/Box';
import useTheme from '@rover/kibble/themes/useTheme';
import type { RoverTheme } from '@rover/types/src/RoverTheme';

export type MarkerImage = {
  url: string;
  altText: string;
};

type UnorderedListVariant = 'primary' | 'secondary' | 'interactive';

export type UnorderedListVariantMap<T> = {
  [P in UnorderedListVariant]: T;
};

const getColorMap = (colors: RoverTheme['colors']): UnorderedListVariantMap<string> => ({
  primary: colors.brand.active_brand,
  interactive: colors.interactive_link.primary_base,
  secondary: colors.neutral.black,
});

export type Props = Omit<BoxProps, 'webAs'> & {
  children: React.ReactNode;
  centered?: boolean;
  icon?: Icons;
  listVariant?: UnorderedListVariant;
};

type IconListItemProps = {
  children: React.ReactNode;
  icon: Icons;
  listVariant?: UnorderedListVariant;
  /** Overrides the list's icon for this item only, e.g. a per-item marker from the API. */
  markerImage?: MarkerImage;
};

export enum Icons {
  CheckMark,
}

type KibbleImageProps = React.ComponentProps<typeof Image>;

/**
 * Kibble's `Image` hands any prop it does not recognise to the underlying `<img>` (web) or RN
 * `Image`, but types neither's `onError`. Same escape hatch as ImageGallery/ImageWithShimmer.tsx.
 */
type PassthroughImageProps = Omit<KibbleImageProps, 'webProps' | 'rxnProps'> & {
  webProps?: KibbleImageProps['webProps'] & { onError?: () => void };
  rxnProps?: KibbleImageProps['rxnProps'] & { onError?: () => void };
};

const MarkerImage = Image as unknown as React.ComponentType<PassthroughImageProps>;

type ListItemProps = Pick<BoxProps, 'ml' | 'sx' | 'mb'> & {
  webAs?: 'li';
  centered?: boolean;
  listVariant?: UnorderedListVariant;
  children: React.ReactNode;
};

export const ListItem: React.FC<ListItemProps> = ({
  centered = false,
  listVariant = 'primary',
  children,
  ...props
}) => {
  const { colors } = useTheme();

  return (
    <Box
      sx={{
        '&::marker': {
          color: getColorMap(colors)[listVariant],
        },
        ...(centered && { listStylePosition: 'inside' }),
      }}
      {...props}
    >
      {children}
    </Box>
  );
};

const IconListItem = ({
  icon,
  children,
  listVariant = 'primary',
  markerImage,
}: IconListItemProps): JSX.Element => {
  const { colors } = useTheme();
  // Remembering which url failed, rather than that one did, lets a new/changed marker image get
  // its own chance to load rather than staying stuck on a previous failure.
  const [failedUrl, setFailedUrl] = useState<string | undefined>(undefined);
  const markerImageFailed = markerImage?.url !== undefined && failedUrl === markerImage.url;

  const iconResources = useMemo(
    () => ({
      [Icons.CheckMark]: (
        <Check
          data-qa-id="unordered-list-check"
          width="30px"
          height="30px"
          viewBox="4 4 24 24"
          fill={getColorMap(colors)[listVariant]}
        />
      ),
    }),
    [colors, listVariant]
  );

  return (
    <Flex webAs="li" mb="2x" flexDirection="row">
      <Flex justifyContent="center" minWidth="30px">
        {markerImage && !markerImageFailed ? (
          <MarkerImage
            src={markerImage.url}
            description={markerImage.altText}
            width="30px"
            height="30px"
            resizeMethod="contain"
            webProps={{ onError: () => setFailedUrl(markerImage.url) }}
            rxnProps={{ onError: () => setFailedUrl(markerImage.url) }}
          />
        ) : (
          iconResources[icon]
        )}
      </Flex>
      <ListItem ml="6x">{children}</ListItem>
    </Flex>
  );
};

const UnorderedList = ({ centered = false, ...props }: Props): JSX.Element => {
  let ulPadding;
  let paddingInlineStart;
  if (props.icon !== undefined) {
    ulPadding = 0;
    paddingInlineStart = 0;
  }
  if (props.listVariant === 'secondary') {
    ulPadding = 0;
    paddingInlineStart = '25px';
  }

  return (
    <Box
      sx={{
        textAlign: centered ? 'center' : undefined,
        padding: ulPadding,
        paddingInlineStart,
      }}
      pl={centered ? '0x' : undefined}
      {...props}
      webAs="ul"
    >
      {React.Children.map(props.children, (item) => {
        // A per-item marker image (e.g. from API content) overrides the list's own icon for
        // that item only, so it's read off the child's own props rather than passed via
        // UnorderedList's props.
        const markerImage = React.isValidElement<{ markerImage?: MarkerImage }>(item)
          ? item.props.markerImage
          : undefined;

        return props.icon === undefined ? (
          <ListItem webAs="li" mb="2x" centered={centered} listVariant={props.listVariant}>
            {item}
          </ListItem>
        ) : (
          <IconListItem icon={props.icon} listVariant={props.listVariant} markerImage={markerImage}>
            {item}
          </IconListItem>
        );
      })}
    </Box>
  );
};

export default UnorderedList;
